'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef } from 'react';
import { loadImage } from '@/shared/lib/image';
import { imageCache } from '@/shared/lib/image-cache';
import { ASSET_DND_MIME, type AssetDragItem, LEGACY_DND_MIME, serializePayload } from './asset-dnd';
interface UseAssetDragParams {
  selectedIds: Set<string>;
  getItemsById: (ids: string[]) => AssetDragItem[];
}
interface DragProps {
  draggable: true;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
}
const BADGE_SIZE = 24;
const BADGE_OFFSET = 4;
function createDragPreview(thumbnail: HTMLElement, count: number): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = '-9999px';
  wrapper.style.left = '-9999px';
  wrapper.style.pointerEvents = 'none';
  const clone = thumbnail.cloneNode(true) as HTMLElement;
  clone.style.width = `${thumbnail.offsetWidth}px`;
  clone.style.height = `${thumbnail.offsetHeight}px`;
  wrapper.appendChild(clone);
  if (count > 1) {
    const badge = document.createElement('div');
    badge.textContent = String(count);
    Object.assign(badge.style, {
      position: 'absolute',
      top: `-${BADGE_OFFSET}px`,
      right: `-${BADGE_OFFSET}px`,
      width: `${BADGE_SIZE}px`,
      height: `${BADGE_SIZE}px`,
      borderRadius: '50%',
      background: 'var(--fill-primary, #3366FF)',
      color: '#fff',
      fontSize: '12px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '1'
    });
    wrapper.appendChild(badge);
  }
  document.body.appendChild(wrapper);
  return wrapper;
}
export function useAssetDrag(t0) {
  const $ = _c(6);
  const {
    selectedIds,
    getItemsById
  } = t0;
  const ghostRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (ghostRef.current) {
        document.body.removeChild(ghostRef.current);
        ghostRef.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cleanupGhost = t1;
  let t2;
  if ($[1] !== getItemsById || $[2] !== selectedIds) {
    t2 = item => ({
      draggable: true,
      onDragStart: e => {
        if (!item.src) {
          e.preventDefault();
          return;
        }
        const isSelected = selectedIds.has(item.id);
        let items;
        if (isSelected && selectedIds.size > 1) {
          items = getItemsById(Array.from(selectedIds));
        } else {
          items = [item];
        }
        const serialized = serializePayload(items);
        e.dataTransfer.setData(ASSET_DND_MIME, serialized);
        e.dataTransfer.setData(LEGACY_DND_MIME, serialized);
        if (items.length === 1) {
          e.dataTransfer.setData("text/plain", items[0].src);
        }
        e.dataTransfer.effectAllowed = "copy";
        const thumbEl = e.currentTarget;
        if (items.length > 1) {
          const ghost = createDragPreview(thumbEl, items.length);
          ghostRef.current = ghost;
          e.dataTransfer.setDragImage(ghost, thumbEl.offsetWidth / 2, thumbEl.offsetHeight / 2);
        }
        for (const it of items) {
          imageCache.preload(it.src, () => loadImage(it.src));
        }
      },
      onDragEnd: () => {
        cleanupGhost();
      }
    });
    $[1] = getItemsById;
    $[2] = selectedIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getDragProps = t2;
  let t3;
  if ($[4] !== getDragProps) {
    t3 = {
      getDragProps
    };
    $[4] = getDragProps;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}